import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/scenario_pages_plan.md — "The Iron That Wouldn't Absorb".

export const ironAmara: Scenario = {
  slug: 'iron-amara',
  series: 'Nutrient Absorption',
  title: "She kept eating iron. Her stores stayed low.",
  socialHook:
    "Amara, 29: exhausted for two years, eating red meat, and still told she was only 'borderline.' The clue was not iron intake. It was absorption.",
  meta: {
    description:
      "A fictional case study: Amara's iron stores stayed low despite a meat-rich diet. Her TMPRSS6 and SLC11A2 results explain why her gut was not absorbing enough iron, and why the next conversation changed.",
  },

  persona: {
    name: 'Amara',
    age: 29,
    sex: 'Female',
    ancestry: 'West African',
    occupation: 'Nurse',
    chiefConcern:
      "Exhausted for two years with low ferritin, but repeatedly told she is not anemic. She eats red meat three to four times a week and cannot understand why her iron stores are not moving.",
    familyHistory: "Mother: similar fatigue and borderline-low iron for many years before it was investigated.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent fatigue — present most days for two years, worse in the afternoon',
      'Brain fog: difficulty concentrating during long shifts',
      'Breathlessness on exertion — climbing stairs, running between wards',
      'Cold hands and feet even in warm environments',
    ],
    labs: [
      {
        name: 'Ferritin (iron stores)',
        value: '14 µg/L',
        refRange: '>20 µg/L',
        flag: 'low',
      },
      {
        name: 'Serum iron',
        value: '9 µmol/L',
        refRange: '10–30 µmol/L',
        flag: 'low',
      },
    ],
    medications: ['No prescription medications'],
    supplements: ['No current iron supplementation'],
    lifestyle: [
      'Diet: red meat 3–4 times per week; low fruit intake; rarely eats vitamin-C-rich foods',
      'Regular tea and coffee drinker — often with meals',
      'Non-smoker, alcohol 1–2 drinks/week',
      'Exercise: walking to and from work; no formal training',
      'Sleep: 7–8 hours; unrefreshing',
    ],
  },

  genetics: [
    {
      gene: 'TMPRSS6',
      variant: 'rs855791 (A allele)',
      rsid: 'rs855791',
      genotype: 'A allele carrier',
      plainMeaning:
        "Amara's gut may keep getting a 'hold back iron' signal even when her iron stores are already low.",
      technicalDetail:
        "TMPRSS6 makes a protein whose job is to suppress hepcidin — the hormone that tells the gut to stop absorbing iron. Normally when iron stores fall, TMPRSS6 dials hepcidin down and the gut opens up. The rs855791 A allele blunts that suppression, so hepcidin stays elevated even when ferritin is critically low. Eating more iron does not fix a regulatory problem — the gate is being held closed by a hormone signal that never gets corrected.",
    },
    {
      gene: 'SLC11A2 / DMT1',
      variant: 'rs224589',
      rsid: 'rs224589',
      genotype: 'Reduced-function variant',
      plainMeaning:
        "A second issue means less iron crosses from her gut into her blood, even when iron is available in her meals.",
      technicalDetail:
        "SLC11A2 encodes DMT1, the transporter protein that physically moves iron molecules across the gut wall. A reduced-function variant here means the transporter works less efficiently — less iron gets through even when hepcidin is not actively blocking. This is an independent bottleneck: even if the TMPRSS6 regulatory issue were corrected, she would still absorb less iron than someone without this variant.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Two years of 'borderline'",
      body: "Amara is 29 and has been exhausted for two years. A 12-hour nursing shift leaves her wiped out, and sleep does not fix it. Her iron has been checked three times. Each time, her ferritin is low, but she is told she is not anemic yet, so nothing changes. She eats red meat three to four times a week and has tried adding more spinach. As a nurse, she can read her labs. What she cannot explain is why doing what she was told has not moved the number.",
    },
    {
      type: 'narrative',
      heading: "The lab clue was already there",
      body: "Her iron stores are truly low, and they are low enough to fit the fatigue, brain fog, and breathlessness she feels. The phrase 'not anemic' is technically true, but it misses the point. Iron deficiency can cause symptoms before it becomes iron-deficiency anemia. Amara has been running on depleted stores for two years.",
    },
    {
      type: 'reveal',
      heading: "The problem was not intake",
      body: "The issue was not that Amara failed to eat enough iron. The issue was that her gut was being told not to absorb enough of it. Hepcidin is the hormone that controls the iron gate in the gut. When iron stores fall, hepcidin should drop so more iron gets in. Her TMPRSS6 result helps explain why that signal may stay too high, even when ferritin is low. More steak was never going to fix a gate that stayed partly closed.",
    },
    {
      type: 'reveal',
      heading: "A second absorption clue",
      body: "Even if the first signal quiets down, a second step can still slow her down. SLC11A2/DMT1 is one of the transporters that moves iron across the gut wall into the bloodstream. Her result suggests that transporter works less efficiently. Together, the two findings point to the same answer: the bottleneck is in absorption, not effort. That changes the treatment conversation, including whether iron needs to bypass the gut.",
    },
    {
      type: 'action',
      heading: "Five things to discuss with her clinician",
      items: [
        "Ask whether an iron infusion is appropriate. IV iron bypasses the gut, so it skips both the hepcidin gate and the DMT1 transporter issue.",
        "If oral iron is tried first, ask about ferrous bisglycinate, sometimes labeled 'iron bisglycinate' or 'gentle iron.' It may be better tolerated and uses a partly different absorption route.",
        "Take oral iron with vitamin C if her clinician recommends supplements. Vitamin C helps iron stay in a form the gut can absorb and may support absorption when hepcidin is high.",
        "Keep tea and coffee away from iron doses and iron-rich meals. Tannins bind iron in the gut before it can be absorbed.",
        "Set a recheck plan and a ferritin target that is comfortably normal, not just barely above the cutoff. The goal is symptom recovery, not just avoiding anemia.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Amara's 14-day energy check-in",
      goalLabel: 'Daily energy, brain fog & breathlessness',
      caption:
        "Amara received an IV iron infusion on day 1. The first week did not feel dramatic, because iron takes time to rebuild stores. By day 9, the pattern started to change.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Energy', value: 2 },
            { label: 'Brain fog', value: 8 },
            { label: 'Breathlessness', value: 7 },
          ],
          note: "Infusion done. No change yet — knew it wouldn't be instant.",
        },
        {
          day: 2,
          scores: [
            { label: 'Energy', value: 2 },
            { label: 'Brain fog', value: 8 },
            { label: 'Breathlessness', value: 7 },
          ],
          note: "Same as always. Long shift. Crashed when I got home.",
        },
        {
          day: 3,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Brain fog', value: 7 },
            { label: 'Breathlessness', value: 7 },
          ],
          note: "Day off. Rested most of it. Hard to separate rest from anything else.",
        },
        {
          day: 4,
          scores: [
            { label: 'Energy', value: 2 },
            { label: 'Brain fog', value: 8 },
            { label: 'Breathlessness', value: 8 },
          ],
          note: "Back on shift. Stairs were still a problem. Nothing changed.",
        },
        {
          day: 5,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Brain fog', value: 7 },
            { label: 'Breathlessness', value: 7 },
          ],
          note: "Maybe slightly less foggy at the end of shift. Could be wishful thinking.",
        },
        {
          day: 6,
          scores: [
            { label: 'Energy', value: 3 },
            { label: 'Brain fog', value: 6 },
            { label: 'Breathlessness', value: 6 },
          ],
          note: "Noticed I wasn't completely done by 2pm. Small thing but I noticed.",
        },
        {
          day: 7,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Brain fog', value: 6 },
            { label: 'Breathlessness', value: 6 },
          ],
          note: "Walked up two flights without stopping to catch my breath. First time in months.",
        },
        {
          day: 8,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Brain fog', value: 6 },
            { label: 'Breathlessness', value: 5 },
          ],
          note: "Something is shifting. Still tired but it feels different — like tired-from-work, not baseline exhausted.",
        },
        {
          day: 9,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Brain fog', value: 4 },
            { label: 'Breathlessness', value: 5 },
          ],
          note: "Got to the end of my shift and had energy left. That hasn't happened in two years.",
          milestone: "First day without afternoon crash",
        },
        {
          day: 10,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Brain fog', value: 4 },
            { label: 'Breathlessness', value: 4 },
          ],
          note: "Ran for the bus. Caught it. Didn't need five minutes to recover.",
        },
        {
          day: 11,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Brain fog', value: 3 },
            { label: 'Breathlessness', value: 4 },
          ],
          note: "Sharp on ward round. Felt like my brain was actually working.",
        },
        {
          day: 12,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Brain fog', value: 3 },
            { label: 'Breathlessness', value: 3 },
          ],
          note: "Two back-to-back shifts and I'm not completely destroyed. That's new.",
        },
        {
          day: 13,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Brain fog', value: 3 },
            { label: 'Breathlessness', value: 3 },
          ],
          note: "Day off. Actually did things. Didn't spend it in bed.",
        },
        {
          day: 14,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Brain fog', value: 2 },
            { label: 'Breathlessness', value: 3 },
          ],
          note: "Two weeks. I feel like myself again. Going to book the recheck ferritin.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'iron-amara',
    label: 'Explore your iron absorption genetics in Coach',
  },
}
